import { useEffect, useRef, useState } from "react";
import "../../styles/gifting.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const CALENDLY_URL = "https://calendly.com/shubham-shadowlabs/30min";

// Standalone landing page for the Trigrly gifting concept. Deliberately does NOT
// use SiteLayout / base.css: the page carries its own premium Sana-class theme, not
// Shadow's brand, so it owns its own nav, footer, and tokens under .gifting-page.
// Copy style note: no em dashes anywhere on this page, per brand voice.

// Booking step. Shown only AFTER the email is captured and the Slack
// notification has fired, so the flow is: waitlist -> Slack -> pick a time.
// An in-page modal with an inline Calendly embed avoids popup blockers and
// keeps the visitor on the landing page.
function BookingModal({ open, onClose }) {
  const calRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // A bare Calendly iframe stays blank because the inline embed waits for a
  // handshake from Calendly's widget.js. Load that script once and let it
  // build the widget so the calendar renders reliably.
  useEffect(() => {
    if (!open) return undefined;
    const container = calRef.current;
    if (!container) return undefined;
    let cancelled = false;
    const render = () => {
      if (cancelled || !window.Calendly || !container) return;
      container.innerHTML = "";
      window.Calendly.initInlineWidget({
        url: `${CALENDLY_URL}?hide_gdpr_banner=1`,
        parentElement: container,
      });
    };
    if (window.Calendly) {
      render();
    } else {
      const SRC = "https://assets.calendly.com/assets/external/widget.js";
      let script = document.querySelector(`script[src="${SRC}"]`);
      if (!script) {
        script = document.createElement("script");
        script.src = SRC;
        script.async = true;
        document.body.appendChild(script);
      }
      script.addEventListener("load", render, { once: true });
    }
    return () => {
      cancelled = true;
      if (container) container.innerHTML = "";
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="gf-modal-backdrop" onClick={onClose}>
      <div
        className="gf-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Pick a time to talk"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="gf-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <div className="gf-modal-head">
          <div className="gf-kicker">You're on the list · last step</div>
          <h3>Pick a time to talk</h3>
        </div>
        <div className="gf-modal-cal" ref={calRef} />
        <div className="gf-modal-foot">
          Trouble loading?{" "}
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
            Open Calendly in a new tab →
          </a>
        </div>
      </div>
    </div>
  );
}

function WaitlistForm({ placement, inputRef }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | submitting | done | duplicate | error
  const [showBooking, setShowBooking] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value || state === "submitting") return;
    setState("submitting");
    try {
      const res = await fetch(`${BACKEND_URL}/api/platform/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: value, use_case: `Gifting access request · ${placement}` }),
      });
      if (!res.ok) throw new Error(`waitlist request failed: ${res.status}`);
      const data = await res.json();
      setState(data.status === "already_registered" ? "duplicate" : "done");
      // Email is captured and Slack has been notified; now surface Calendly.
      setShowBooking(true);
    } catch (err) {
      console.error("[trigrly] waitlist submit failed", err);
      setState("error");
    }
  };

  if (state === "done" || state === "duplicate") {
    return (
      <>
        <div className="gf-form-done">
          <span className="gf-form-done-dot" aria-hidden="true" />
          <span>
            {state === "done" ? "You're in. " : "Welcome back. "}
            <button type="button" className="gf-form-book" onClick={() => setShowBooking(true)}>
              Pick a time to talk →
            </button>
          </span>
        </div>
        <BookingModal open={showBooking} onClose={() => setShowBooking(false)} />
      </>
    );
  }

  return (
    <form className="gf-form" onSubmit={submit}>
      <div className="gf-form-pill">
        <input
          ref={inputRef}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Work email"
          aria-label="Work email"
        />
        <button type="submit" className="gf-btn gf-btn-accent" disabled={state === "submitting"}>
          {state === "submitting" ? "Requesting…" : "Request access"}
        </button>
      </div>
      {state === "error" && (
        <div className="gf-form-error">
          Something went wrong saving your email. You can still{" "}
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
            pick a time to talk →
          </a>{" "}
          or email <a href="mailto:hello@shadowlabs.ai">hello@shadowlabs.ai</a>.
        </div>
      )}
    </form>
  );
}

/* ------------------------------------------------------------------ */
/* Device frames                                                       */
/* ------------------------------------------------------------------ */

function IPhone({ className = "", children }) {
  return (
    <div className={`gf-iphone ${className}`} aria-hidden="true">
      <span className="gf-iphone-btn gf-iphone-btn-power" />
      <span className="gf-iphone-btn gf-iphone-btn-vol1" />
      <span className="gf-iphone-btn gf-iphone-btn-vol2" />
      <div className="gf-iphone-screen">
        <div className="gf-iphone-status">
          <span className="gf-iphone-time">9:41</span>
          <span className="gf-iphone-island" />
          <span className="gf-iphone-glyphs">
            <span className="gf-glyph-wifi" />
            <span className="gf-glyph-batt" />
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}

function MacBook({ children }) {
  return (
    <div className="gf-macbook" aria-hidden="true">
      <div className="gf-macbook-screen">
        <span className="gf-macbook-cam" />
        {children}
      </div>
      <div className="gf-macbook-base">
        <span className="gf-macbook-notch" />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Hero: looping Slack conversation inside a desktop app frame         */
/* ------------------------------------------------------------------ */

// Steps: 0 typing · 1 context msg · 2 suggestion + policy checks · 3 human "Send it"
// · 4 confirmation · 5 hold, then loop.
const STEP_MS = [900, 1500, 2600, 1300, 1700, 3200];

function useChatLoop() {
  const [step, setStep] = useState(0);
  const reduced = useRef(
    typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    if (reduced.current) {
      setStep(5);
      return;
    }
    const id = setTimeout(() => setStep((s) => (s + 1) % STEP_MS.length), STEP_MS[step]);
    return () => clearTimeout(id);
  }, [step]);
  return step;
}

function ChatMessages({ step }) {
  return (
    <>
      {step === 0 && (
        <div className="gf-msg gf-pop">
          <div className="gf-avatar gf-avatar-app">T</div>
          <div className="gf-typing">
            <span />
            <span />
            <span />
          </div>
        </div>
      )}
      {step >= 1 && (
        <div className="gf-msg gf-pop">
          <div className="gf-avatar gf-avatar-app">T</div>
          <div>
            <div className="gf-msg-meta">
              Trigrly <span className="gf-app-tag">APP</span>
              <span className="gf-msg-time">9:14 AM</span>
            </div>
            <div className="gf-msg-text">
              Meridian Capital just closed Fund IV. Dana Whitfield led it. Suggest: a bottle of
              Barolo from the fund's launch vintage, ~$85, from a Piedmont importer near her
              office.
            </div>
          </div>
        </div>
      )}
      {step >= 2 && (
        <div className="gf-msg gf-pop">
          <div className="gf-avatar gf-avatar-app">T</div>
          <div>
            <div className="gf-policy">
              <div className="gf-policy-row">
                <span className="gf-check">✓</span> Within $300 annual cap · $85
              </div>
              <div className="gf-policy-row">
                <span className="gf-check">✓</span> Firm-wide aggregate: $145 of $300
              </div>
              <div className="gf-policy-row">
                <span className="gf-check">✓</span> Cleared, no pre-approval required
              </div>
            </div>
            <div className="gf-slack-actions">
              <span className={`gf-slack-btn gf-slack-btn-primary ${step === 3 ? "pressed" : ""}`}>
                Send it
              </span>
              <span className="gf-slack-btn">Swap gift</span>
            </div>
          </div>
        </div>
      )}
      {step >= 4 && (
        <div className="gf-msg gf-pop">
          <div className="gf-avatar gf-avatar-app">T</div>
          <div>
            <div className="gf-msg-meta">
              Trigrly <span className="gf-app-tag">APP</span>
              <span className="gf-msg-time">9:15 AM</span>
            </div>
            <div className="gf-msg-text">
              Sent 🎁 Delivery Thursday. Record <strong>#TR-2481</strong> filed.
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SlackApp() {
  const step = useChatLoop();
  return (
    <div className="gf-slackapp">
      <div className="gf-slackapp-side">
        <div className="gf-slackapp-ws">Meridian Partners</div>
        <div className="gf-slackapp-group">Channels</div>
        <div className="gf-slackapp-ch"># gtm-team</div>
        <div className="gf-slackapp-ch gf-slackapp-active"># client-team</div>
        <div className="gf-slackapp-ch"># deals</div>
        <div className="gf-slackapp-ch"># wins</div>
        <div className="gf-slackapp-group">Apps</div>
        <div className="gf-slackapp-ch gf-slackapp-appitem">
          <span className="gf-slackapp-appdot">T</span> Trigrly
        </div>
      </div>
      <div className="gf-slackapp-main">
        <div className="gf-slackapp-head">
          <strong># client-team</strong>
          <span>Dana · Meridian · Q3</span>
        </div>
        <div className="gf-slack-body gf-slackapp-body">
          <ChatMessages step={step} />
        </div>
      </div>
    </div>
  );
}

/* Phone screens */

function ApproveScreen() {
  return (
    <div className="gf-phone-approve">
      <div className="gf-phone-app-head">Dana Whitfield · Meridian</div>
      <div className="gf-gift-card">
        <div className="gf-gift-emoji">🍷</div>
        <div>
          <div className="gf-gift-name">Barolo, 2021 vintage</div>
          <div className="gf-gift-sub">$85 · cleared by policy</div>
        </div>
      </div>
      <div className="gf-approve">Approve &amp; send</div>
      <div className="gf-phone-fine">Aggregate after send: $230 of $300</div>
    </div>
  );
}

function LockScreen() {
  return (
    <div className="gf-phone-lock">
      <div className="gf-lock-time">9:41</div>
      <div className="gf-lock-date">Tuesday, August 4</div>
      <div className="gf-push">
        <div className="gf-push-head">
          <span className="gf-push-icon">T</span> TRIGRLY · now
        </div>
        <div className="gf-push-title">Client moment spotted</div>
        <div className="gf-push-body">Meridian closed Fund IV. A gift for Dana is ready.</div>
      </div>
      <div className="gf-push">
        <div className="gf-push-head">
          <span className="gf-push-icon">T</span> TRIGRLY · 1h ago
        </div>
        <div className="gf-push-title">Crestline renewal in 30 days</div>
        <div className="gf-push-body">Anna Ross. A thank-you before the renewal call?</div>
      </div>
      <div className="gf-push gf-push-dim">
        <div className="gf-push-head">
          <span className="gf-push-icon">T</span> TRIGRLY · 2d ago
        </div>
        <div className="gf-push-title">Gift delivered</div>
        <div className="gf-push-body">Record #TR-2470 filed to books &amp; records.</div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Content data                                                        */
/* ------------------------------------------------------------------ */

const INTEGRATIONS = [
  "Salesforce",
  "HubSpot",
  "Slack",
  "Gmail",
  "Outlook",
  "Google Calendar",
  "Gong",
  "Zoom",
];

const PROBLEMS = [
  {
    title: "Expensive platforms",
    body: "Annual contracts, per-seat fees, and marked-up merchandise before a single gift ships.",
  },
  {
    title: "Missed moments",
    body: "The fund closed last week. The promotion was a month ago. The gift never went.",
  },
  {
    title: "No personalization",
    body: "The same branded fleece for every client on the list, whoever they are.",
  },
  {
    title: "Clunky dashboards",
    body: "Another portal, another login, another tool your team opens twice and abandons.",
  },
];

const VALUES = [
  {
    n: "01",
    title: "Proactive",
    line: "Every client moment, spotted.",
    body: "Fund closes, promotions, funding rounds, renewals. Trigrly watches your book and pings you the moment a client deserves more than an email.",
    vignette: (
      <div className="gf-vig">
        <div className="gf-signal gf-signal-live">
          <span className="gf-signal-dot" />
          <span className="gf-signal-label">Fund IV closed</span>
          <span className="gf-signal-time">2m</span>
        </div>
        <div className="gf-signal">
          <span className="gf-signal-dot" />
          <span className="gf-signal-label">Series C announced</span>
          <span className="gf-signal-time">1h</span>
        </div>
        <div className="gf-signal">
          <span className="gf-signal-dot" />
          <span className="gf-signal-label">Renewal opens</span>
          <span className="gf-signal-time">30d</span>
        </div>
      </div>
    ),
  },
  {
    n: "02",
    title: "Personal",
    line: "Context from your whole stack.",
    body: "Suggestions drawn from CRM notes, call transcripts, email threads, and the last meeting. A gift that proves you know them, not a catalog SKU.",
    vignette: (
      <div className="gf-vig">
        <div className="gf-source">
          <span className="gf-source-name">Salesforce</span>
          <span className="gf-source-detail">Led Fund IV</span>
        </div>
        <div className="gf-source">
          <span className="gf-source-name">Gong</span>
          <span className="gf-source-detail">Piedmont wine fan</span>
        </div>
        <div className="gf-source">
          <span className="gf-source-name">Calendar</span>
          <span className="gf-source-detail">Met Tuesday</span>
        </div>
        <div className="gf-source">
          <span className="gf-source-name">News</span>
          <span className="gf-source-detail">$2.1B close</span>
        </div>
      </div>
    ),
  },
  {
    n: "03",
    title: "Mobile-native",
    line: "Approve from your pocket.",
    body: "A push notification, a glance, a tap. No portal, no login, no training. Gifting at the speed of the deal, wherever the deal finds you.",
    vignette: (
      <div className="gf-vig gf-vig-notif">
        <div className="gf-notif">
          <div className="gf-notif-icon">T</div>
          <div className="gf-notif-body">
            <div className="gf-notif-head">
              <span className="gf-notif-app">Trigrly</span>
              <span className="gf-notif-time">now</span>
            </div>
            <div className="gf-notif-text">Dana's gift cleared. Tap to send.</div>
            <div className="gf-notif-meta">Barolo 2021 · $85 · cleared</div>
          </div>
        </div>
      </div>
    ),
  },
];

const CAPABILITIES = [
  {
    n: "01",
    title: "Slack assistant & MCP server",
    body: "Ask in Slack, or connect Claude and ChatGPT over MCP. Anyone on the team, and any agent, can send within policy.",
  },
  {
    n: "02",
    title: "Teams & budgets",
    body: "Org, team, and per-rep budgets with expense controls baked in. Role-based access for requesters, approvers, and admins.",
  },
  {
    n: "03",
    title: "Approval layer",
    body: "Review and approve before anything is bought, with rules that match how your org actually spends.",
  },
  {
    n: "04",
    title: "Integrations",
    body: "Salesforce, HubSpot, Gong, Gmail, Outlook, Calendar. Trigrly reads the moments your team already creates and writes back the record.",
  },
  {
    n: "05",
    title: "Local merchants",
    body: "Gifts sourced from local makers and merchants near your client, chosen for the person. Nothing pulled from a warehouse catalog.",
  },
  {
    n: "06",
    title: "Premium experience",
    body: "White-glove gifts, packaging, and notes. An experience your clients remember, under your name.",
  },
  {
    n: "07",
    title: "Live tracking & records",
    body: "Every gift tracked from approval to doorstep, with the record filed automatically on delivery.",
  },
  {
    n: "08",
    title: "Compliance layer",
    body: "For teams under FINRA, SEC, or FCPA rules: caps, firm-wide aggregation, and books & records handled without a spreadsheet.",
  },
];

const MOBILE_POINTS = [
  {
    title: "A push the moment it matters",
    body: "The fund closes at 9:12. The suggestion is on your lock screen at 9:14, with the policy already cleared.",
  },
  {
    title: "One tap to approve",
    body: "The gift, the price, the policy check, and the client's running total, on one screen built for a thumb.",
  },
  {
    title: "The record files itself",
    body: "By the time the phone is back in your pocket, books and records are done and your CCO can see it.",
  },
];

const PROMPT_POINTS = [
  {
    title: "One line, whole segment",
    body: "“Every client who closed a fund this quarter, something under $100.” Trigrly resolves the list from your CRM and matches a gift to each person.",
  },
  {
    title: "Policy runs inline",
    body: "Every recipient is checked against caps and firm-wide aggregates. Anything near a limit is held for review, never silently sent.",
  },
  {
    title: "You stay the decision-maker",
    body: "The whole play stages for one approval. Approve in Slack or from your phone; records file themselves on delivery.",
  },
];

const AGENT_POINTS = [
  {
    title: "AI-native core",
    body: "Every decision is a model reasoning over your policy and client context. Not a dropdown, not a workflow builder.",
  },
  {
    title: "Conversation, not console",
    body: "For your team, the product is a Slack thread and a push notification. There is no dashboard to learn.",
  },
  {
    title: "Claude, ChatGPT, or your own agents",
    body: "Connect Trigrly to Claude or ChatGPT over MCP, or wire up your AI SDR through the API. An agent can open the account, load the policy, and place gifts itself. Gifting becomes a capability, not a chore.",
  },
  {
    title: "Gifting as a service, in your product",
    body: "Embed Trigrly through the API: wealth platforms, CRMs, and client-engagement tools ship gifting under their own brand, with our policy engine, personalization, and fulfillment rails underneath.",
  },
];

const AGENT_CHIPS = ["MCP", "Claude", "ChatGPT", "OpenAI Agents SDK", "REST API", "Embed API"];

const COMPARE = {
  columns: [
    { name: "Trigrly", sub: "AI-native gifting" },
    { name: "Gifting platforms", sub: "Sendoso · Reachdesk · Snappy" },
    { name: "Compliance suites", sub: "StarCompliance · MCO" },
  ],
  rows: [
    ["Spots client moments proactively", "yes", "no", "no"],
    ["Personalized from client context", "yes", "no", "no"],
    ["Slack + mobile native", "yes", "no", "no"],
    ["CRM sync & delivery tracking", "yes", "yes", "no"],
    ["Campaigns & ABM plays", "part:Agent-run", "part:Manual setup", "no"],
    ["Policy check before every send", "yes", "no", "part:Monitors only"],
    ["Books & records, written automatically", "yes", "no", "part:Manual entry"],
    ["Usable by AI agents (API + MCP)", "yes", "part:API only", "no"],
    ["Gifting as a service (embed API)", "yes", "part:API only", "no"],
    ["Self-serve, live in a day", "yes", "part:Sales-led", "part:Sales-led"],
  ],
};

function CompareCell({ value, us }) {
  if (value === "yes") return <span className="gf-cmp-yes">✓</span>;
  if (value === "no") return <span className="gf-cmp-no">—</span>;
  const label = value.replace("part:", "");
  return <span className={`gf-cmp-part ${us ? "gf-cmp-part-us" : ""}`}>{label}</span>;
}

const ENGINE_ROWS = [
  ["Recipient type", "Client, prospect, public official, union official, each with its own rules"],
  ["Jurisdiction", "US federal, state, and non-US regimes resolved per recipient"],
  ["Regulator", "FINRA 3220, SEC, FCPA, and your own firm policy, layered"],
  ["Value band", "Codified valuation, caps, and thresholds per gift"],
  ["Aggregation window", "Rolling 12-month totals, firm-wide, per recipient"],
  ["Taxability", "Gross-up and reporting flags where they apply"],
];

const API_SNIPPET = `// your agent (Claude, ChatGPT, or your own) via MCP
trigrly.place_gift({
  recipient: "dana@meridiancap.com",
  occasion: "fund_close",
  budget_usd: 100
})

// → cleared, sent, and on the record
{
  "status": "cleared",
  "policy": {
    "rule": "FINRA 3220",
    "cap_usd": 300,
    "aggregate_ytd_usd": 145,
    "review_required": false
  },
  "record_id": "TR-2481",
  "eta": "2026-08-06"
}`;

const FOOTER_COLS = [
  {
    head: "Product",
    links: [
      ["The problem", "#problem"],
      ["What it does", "#product"],
      ["The mobile app", "#mobile-app"],
      ["Prompt plays", "#plays"],
      ["Compare", "#compare"],
    ],
  },
  {
    head: "Platform",
    links: [
      ["Compliance layer", "#compliance"],
      ["Integrations", "#integrations"],
    ],
  },
  {
    head: "Company",
    links: [
      ["Book 20 minutes", CALENDLY_URL],
      ["Request access", "#waitlist"],
      ["hello@shadowlabs.ai", "mailto:hello@shadowlabs.ai"],
    ],
  },
  {
    head: "Legal",
    links: [
      ["Privacy", "/privacy"],
      ["Terms", "/terms"],
    ],
  },
];

/* ------------------------------------------------------------------ */

export default function SiteGifting() {
  const rootRef = useRef(null);
  const heroInputRef = useRef(null);

  useEffect(() => {
    document.title = "Trigrly · AI-native gifting for GTM teams";
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const els = root.querySelectorAll(".gf-reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const goToWaitlist = () => {
    document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth" });
    setTimeout(() => heroInputRef.current?.focus({ preventScroll: true }), 450);
  };

  return (
    <div className="gifting-page" ref={rootRef}>
      <header className="gf-nav">
        <div className="gf-wrap gf-nav-row">
          <span className="gf-wordmark">
            <img src="/trigrly-favicon.svg" alt="" className="gf-wordmark-icon" />
            Trigrly
          </span>
          <div className="gf-nav-actions">
            <a
              className="gf-nav-link"
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Book 20 minutes
            </a>
            <button type="button" className="gf-btn gf-btn-accent gf-btn-sm" onClick={goToWaitlist}>
              Request access
            </button>
          </div>
        </div>
      </header>

      <section className="gf-hero-shell">
        <div className="gf-hero">
          <div className="gf-trust-chip">
            <span className="gf-trust-dot" /> Gifting, done right
          </div>
          <h1>
            Every gift,
            <br />
            <em className="gf-serif">perfectly placed.</em>
          </h1>
          <p className="gf-lede">
            Trigrly plugs into your CRM, inbox, and calendar, watches every client's moments,
            and turns them into policy-cleared gifts, suggested in Slack and approved from your
            phone.
          </p>
          <div id="waitlist" className="gf-hero-form">
            <WaitlistForm placement="hero" inputRef={heroInputRef} />
            <div className="gf-fineprint">FINRA Rule 3220 · SEC · FCPA · SOC 2 controls</div>
          </div>
          <div className="gf-hero-scene">
            <MacBook>
              <SlackApp />
            </MacBook>
            <IPhone className="gf-iphone-hero">
              <ApproveScreen />
            </IPhone>
          </div>
        </div>
      </section>

      <section className="gf-integrations" id="integrations">
        <div className="gf-wrap">
          <div className="gf-kicker gf-reveal">Plugs into your GTM stack</div>
          <div className="gf-logo-row gf-reveal">
            {INTEGRATIONS.map((name) => (
              <span className="gf-logo" key={name}>
                {name}
              </span>
            ))}
          </div>
          <p className="gf-integrations-line gf-reveal">
            Reads the moments your team already creates. Writes back the record.
          </p>
        </div>
      </section>

      <section className="gf-problem" id="problem">
        <div className="gf-wrap">
          <div className="gf-kicker gf-reveal">The problem</div>
          <h2 className="gf-reveal">
            Gifting today is <em className="gf-serif">broken.</em>
          </h2>
          <p className="gf-section-sub gf-reveal">
            Your clients' biggest moments deserve more than what the category offers.
          </p>
          <div className="gf-problem-grid">
            {PROBLEMS.map((p) => (
              <div className="gf-problem-card gf-reveal" key={p.title}>
                <span className="gf-problem-x">✕</span>
                <h3>{p.title}</h3>
                <p>{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="gf-values" id="product">
        <div className="gf-wrap">
          <div className="gf-kicker gf-reveal">The answer</div>
          <h2 className="gf-reveal">
            Three things no gifting tool
            <br />
            has ever <em className="gf-serif">done for you.</em>
          </h2>
          <div className="gf-value-grid">
            {VALUES.map((v) => (
              <div className="gf-value gf-reveal" key={v.n}>
                <div className="gf-value-n">{v.n}</div>
                {v.vignette}
                <h3>
                  {v.title} <span className="gf-value-line gf-serif">{v.line}</span>
                </h3>
                <p>{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="gf-mobile" id="mobile-app">
        <div className="gf-wrap">
          <div className="gf-mobile-card gf-reveal">
            <div className="gf-mobile-copy">
              <div className="gf-kicker">The mobile app</div>
              <h2>
                Your pocket
                <br />
                <em className="gf-serif">approval queue.</em>
              </h2>
              <div className="gf-mobile-points">
                {MOBILE_POINTS.map((p) => (
                  <div className="gf-mobile-point" key={p.title}>
                    <h3>{p.title}</h3>
                    <p>{p.body}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="gf-mobile-phone">
              <IPhone className="gf-iphone-lg">
                <LockScreen />
              </IPhone>
            </div>
          </div>
        </div>
      </section>

      <section className="gf-caps">
        <div className="gf-wrap">
          <div className="gf-kicker gf-reveal">Capabilities</div>
          <h2 className="gf-reveal">
            Everything a gifting program needs.
            <br />
            <em className="gf-serif">Nothing your team has to run.</em>
          </h2>
          <div className="gf-caps-grid">
            {CAPABILITIES.map((c) => (
              <div className="gf-cap gf-reveal" key={c.n}>
                <span className="gf-cap-n">{c.n}</span>
                <div>
                  <h3>{c.title}</h3>
                  <p>{c.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="gf-prompt" id="plays">
        <div className="gf-wrap">
          <div className="gf-agentic-grid">
            <div className="gf-agentic-copy gf-reveal">
              <div className="gf-kicker">Prompt-based plays</div>
              <h2>
                Say the play.
                <br />
                <em className="gf-serif">Trigrly runs it.</em>
              </h2>
              <div className="gf-agentic-points">
                {PROMPT_POINTS.map((p) => (
                  <div className="gf-agentic-point" key={p.title}>
                    <h3>{p.title}</h3>
                    <p>{p.body}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="gf-window gf-reveal" aria-hidden="true">
              <div className="gf-window-head">
                <span className="gf-slack-dot" />
                <span className="gf-slack-dot" />
                <span className="gf-slack-dot" />
                <span className="gf-window-url">trigrly.com/compose</span>
              </div>
              <div className="gf-window-body">
                <div className="gf-compose-label">Compose a play</div>
                <div className="gf-compose-input">
                  <span>
                    Every client who closed a fund this quarter, something personal, under $100
                  </span>
                  <span className="gf-compose-go">↑</span>
                </div>
                <div className="gf-compose-steps">
                  <div className="gf-compose-step">
                    <span className="gf-check">✓</span> Resolved 14 clients
                    <span className="gf-compose-note">Salesforce</span>
                  </div>
                  <div className="gf-compose-step">
                    <span className="gf-check">✓</span> Matched 14 gifts
                    <span className="gf-compose-note">each one personal</span>
                  </div>
                  <div className="gf-compose-step">
                    <span className="gf-check">✓</span> Policy-checked
                    <span className="gf-compose-note">12 cleared · 2 held for review</span>
                  </div>
                  <div className="gf-compose-step gf-compose-step-wait">
                    <span className="gf-compose-spinner" /> Staged for approval
                    <span className="gf-compose-note">est. $1,140 total</span>
                  </div>
                </div>
                <div className="gf-compose-cta">Review the play →</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="gf-compare" id="compare">
        <div className="gf-wrap">
          <div className="gf-kicker gf-reveal">The field</div>
          <h2 className="gf-reveal">Where the incumbents stop.</h2>
          <p className="gf-section-sub gf-reveal">
            Campaigns, tracking, CRM sync: the incumbents have all of it. Trigrly rebuilds the
            category AI-native. The agent watches your book, runs the play, and keeps the record.
          </p>
          <div className="gf-table-scroll gf-reveal">
            <div className="gf-table">
              <div className="gf-tr gf-tr-head">
                <div className="gf-td gf-td-label" />
                {COMPARE.columns.map((c, i) => (
                  <div className={`gf-td gf-td-col ${i === 0 ? "gf-td-us" : ""}`} key={c.name}>
                    <div className="gf-col-name">{c.name}</div>
                    <div className="gf-col-sub">{c.sub}</div>
                  </div>
                ))}
              </div>
              {COMPARE.rows.map(([label, ...cells]) => (
                <div className="gf-tr" key={label}>
                  <div className="gf-td gf-td-label">{label}</div>
                  {cells.map((cell, i) => (
                    <div className={`gf-td ${i === 0 ? "gf-td-us" : ""}`} key={i}>
                      <CompareCell value={cell} us={i === 0} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="gf-engine" id="compliance">
        <div className="gf-wrap">
          <div className="gf-engine-card gf-reveal">
            <div className="gf-kicker gf-kicker-gold">The compliance layer</div>
            <h2>
              Every gift, checked against the rules that{" "}
              <em className="gf-serif">actually apply.</em>
            </h2>
            <p className="gf-engine-sub">
              Built for teams under FINRA, SEC, or FCPA rules, and for anyone who wants real
              control. Recipient, jurisdiction, value, aggregation, and taxability resolved
              before anything ships, filed after it does.
            </p>
            <div className="gf-engine-rows">
              {ENGINE_ROWS.map(([k, v]) => (
                <div className="gf-engine-row" key={k}>
                  <span className="gf-engine-key">{k}</span>
                  <span className="gf-engine-val">{v}</span>
                </div>
              ))}
            </div>
            <p className="gf-engine-close">
              When the rules change, your policy updates once, and every send obeys it.
            </p>
          </div>
        </div>
      </section>

      <section className="gf-cta">
        <div className="gf-wrap">
          <h2 className="gf-reveal">
            Be first in <em className="gf-serif">line.</em>
          </h2>
          <p className="gf-lede gf-reveal">
            We're onboarding a first cohort of design partners: GTM and client teams who want
            gifting that runs itself.
          </p>
          <div className="gf-cta-form gf-reveal">
            <WaitlistForm placement="footer" />
          </div>
        </div>
      </section>

      <footer className="gf-footer">
        <div className="gf-wrap">
          <div className="gf-footer-grid">
            <div className="gf-footer-brand">
              <span className="gf-wordmark">
            <img src="/trigrly-favicon.svg" alt="" className="gf-wordmark-icon" />
            Trigrly
          </span>
              <p>
                The AI-native gifting layer for GTM teams. Proactive, personal, and on the
                record.
              </p>
            </div>
            {FOOTER_COLS.map((col) => (
              <div className="gf-footer-col" key={col.head}>
                <div className="gf-footer-head">{col.head}</div>
                {col.links.map(([label, href]) => {
                  const external = href.startsWith("http");
                  return (
                    <a
                      href={href}
                      key={label}
                      {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    >
                      {label}
                    </a>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="gf-footer-bar">
            <span>© 2026 Converse AI Inc. All rights reserved.</span>
            <span className="gf-footer-badges">
              <span className="gf-footer-badge">SOC 2 controls</span>
              <span className="gf-footer-badge">FINRA 3220 aware</span>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
